import { create } from 'zustand'
import type { ChatMessage, ChatThreadId, ChatType } from '../models/chat.model'

/**
 * In-meeting chat state: one group thread plus one thread per peer.
 *
 * This is the meeting's own chat, carried on the room channel. It is not the
 * org/tenant messenger — no conversation ids, no SDK, nothing shared with
 * `features/messenger`. State is per-session and reset on leave.
 *
 * Two things the flat `messages: ChatMessage[]` it replaced could not do:
 *
 *  1. Hold private threads at all. They lived in `ChatPanel` component state,
 *     so closing the panel (it is lazy-mounted) discarded every private
 *     conversation in the meeting.
 *  2. Count unread per conversation. There was one global counter, cleared on
 *     any render where the message count changed — so a private message that
 *     arrived while you were reading the group tab was marked read without
 *     ever being seen.
 *
 * `activeThread` is what fixes (2): the store knows which conversation is on
 * screen, so an arriving message counts as unread unless the user is actually
 * looking at the thread it belongs to.
 */

type ChatState = {
  /** Room these threads belong to. Null before a meeting is entered. */
  roomId: string | null
  group: ChatMessage[]
  /** Keyed by the OTHER participant's peerId — never by the local user. */
  private: Record<string, ChatMessage[]>
  unreadGroup: number
  unreadPrivate: Record<string, number>
  /** The thread on screen right now, or null when the panel is closed. */
  activeThread: ChatThreadId | null
  typingUsers: string[]
  /** True once the archive has been merged in, so we only fetch once a join. */
  historyLoaded: boolean
  /**
   * The name of each private-chat partner, kept after they leave.
   *
   * A thread is keyed by the other person's peerId, and its name came from the
   * live roster. Once they left, the list fell back to the LAST message's
   * sender — you, whenever you wrote last — and the open thread looked for a
   * message from them and said "Unknown" if there was none. Fed from the roster
   * while they are present (rememberNames) and from their own messages; only
   * ever added to, so leaving cannot erase it.
   */
  partnerNames: Record<string, string>
}

type ChatActions = {
  /** Bind the store to a room, clearing anything held for a different one. */
  setRoomId: (roomId: string) => void
  /** A message off the wire — ours included, since the server echoes it back. */
  receiveMessage: (msg: ChatMessage, localUserId: string) => void
  /** Seed from the meeting's archive. Never counts as unread. */
  loadHistory: (msgs: ChatMessage[], localUserId: string) => void
  /** Open a thread (or null when the panel closes); clears its unread. */
  setActiveThread: (thread: ChatThreadId | null) => void
  markThreadRead: (thread: ChatThreadId) => void
  markAllRead: () => void
  clearThread: (thread: ChatThreadId) => void
  clearByType: (type: ChatType) => void
  clearAll: () => void
  setTyping: (userName: string, isTyping: boolean) => void
  /** Record names for peerIds (the live roster). Never forgets one. */
  rememberNames: (people: ReadonlyArray<{ id: string; name: string }>) => void
}

const INITIAL: ChatState = {
  roomId: null,
  group: [],
  private: {},
  unreadGroup: 0,
  unreadPrivate: {},
  activeThread: null,
  typingUsers: [],
  historyLoaded: false,
  partnerNames: {},
}

/** The thread a message belongs to, from the local user's point of view. */
function threadOf(msg: ChatMessage, localUserId: string): ChatThreadId {
  if (!msg.to) return 'group'
  // A private thread is keyed by the other end, so both directions of the same
  // conversation land in one list.
  return msg.senderId === localUserId ? msg.to : msg.senderId
}

/**
 * `names` with `id` → `name`, or `names` itself when there is nothing new. A
 * name equal to the id is the wire's fallback for a missing name, not a name.
 */
function withPartnerName(names: Record<string, string>, id: string, name: string | undefined): Record<string, string> {
  if (!name || name === id || names[id] === name) return names
  return { ...names, [id]: name }
}

/** Guards against a message arriving twice (socket re-join, history overlap). */
function withMessage(list: ChatMessage[] | undefined, msg: ChatMessage): ChatMessage[] {
  const existing = list ?? []
  if (existing.some((m) => m.id === msg.id)) return existing
  // Ordered by time rather than arrival: history merges under live messages,
  // and a late-delivered message still sits where it belongs.
  const next = [...existing, msg]
  next.sort((a, b) => a.timestamp - b.timestamp)
  return next
}

function markRead(list: ChatMessage[]): ChatMessage[] {
  return list.some((m) => !m.isRead)
    ? list.map((m) => (m.isRead ? m : { ...m, isRead: true }))
    : list
}

export const useChatStore = create<ChatState & ChatActions>((set) => ({
  ...INITIAL,

  setRoomId: (roomId) =>
    set((prev) =>
      prev.roomId === roomId
        ? prev
        : // A different meeting starts clean rather than inheriting.
          { ...INITIAL, roomId, private: {}, unreadPrivate: {}, partnerNames: {} },
    ),

  receiveMessage: (msg, localUserId) =>
    set((prev) => {
      const thread = threadOf(msg, localUserId)
      const own = msg.senderId === localUserId
      // Your own message is never unread, and neither is one in the thread
      // you're currently reading.
      const seen = own || prev.activeThread === thread
      const stored = { ...msg, isRead: seen }

      if (thread === 'group') {
        const group = withMessage(prev.group, stored)
        if (group === prev.group) return prev
        return {
          group,
          unreadGroup: seen ? prev.unreadGroup : prev.unreadGroup + 1,
        }
      }

      const list = withMessage(prev.private[thread], stored)
      if (list === prev.private[thread]) return prev
      return {
        private: { ...prev.private, [thread]: list },
        partnerNames: own ? prev.partnerNames : withPartnerName(prev.partnerNames, msg.senderId, msg.senderName),
        unreadPrivate: seen
          ? prev.unreadPrivate
          : { ...prev.unreadPrivate, [thread]: (prev.unreadPrivate[thread] ?? 0) + 1 },
      }
    }),

  loadHistory: (msgs, localUserId) =>
    set((prev) => {
      // Marked read on arrival: this is what was said before you joined, and
      // opening to a badge of 200 unread is noise, not information.
      let group = prev.group
      const priv = { ...prev.private }

      for (const msg of msgs) {
        const stored = { ...msg, isRead: true }
        const thread = threadOf(stored, localUserId)
        if (thread === 'group') group = withMessage(group, stored)
        else priv[thread] = withMessage(priv[thread], stored)
      }

      return { group, private: priv, historyLoaded: true }
    }),

  setActiveThread: (thread) =>
    set((prev) => {
      if (thread === null) return { activeThread: null }
      if (thread === 'group') {
        return { activeThread: thread, unreadGroup: 0, group: markRead(prev.group) }
      }
      const { [thread]: _dropped, ...restUnread } = prev.unreadPrivate
      return {
        activeThread: thread,
        unreadPrivate: restUnread,
        private: prev.private[thread]
          ? { ...prev.private, [thread]: markRead(prev.private[thread]) }
          : prev.private,
      }
    }),

  markThreadRead: (thread) =>
    set((prev) => {
      if (thread === 'group') return { unreadGroup: 0, group: markRead(prev.group) }
      const { [thread]: _dropped, ...restUnread } = prev.unreadPrivate
      return {
        unreadPrivate: restUnread,
        private: prev.private[thread]
          ? { ...prev.private, [thread]: markRead(prev.private[thread]) }
          : prev.private,
      }
    }),

  markAllRead: () =>
    set((prev) => ({
      group: markRead(prev.group),
      private: Object.fromEntries(
        Object.entries(prev.private).map(([id, list]) => [id, markRead(list)]),
      ),
      unreadGroup: 0,
      unreadPrivate: {},
    })),

  clearThread: (thread) =>
    set((prev) => {
      if (thread === 'group') return { group: [], unreadGroup: 0 }
      const { [thread]: _droppedMsgs, ...restPrivate } = prev.private
      const { [thread]: _droppedUnread, ...restUnread } = prev.unreadPrivate
      return { private: restPrivate, unreadPrivate: restUnread }
    }),

  clearByType: (type) =>
    set((prev) => ({ group: prev.group.filter((m) => m.type !== type), unreadGroup: 0 })),

  clearAll: () => set({ ...INITIAL, private: {}, unreadPrivate: {}, partnerNames: {} }),

  rememberNames: (people) =>
    set((prev) => {
      let next = prev.partnerNames
      for (const p of people) next = withPartnerName(next, p.id, p.name)
      return next === prev.partnerNames ? prev : { partnerNames: next }
    }),

  setTyping: (userName, isTyping) =>
    set((prev) => {
      if (isTyping) {
        return prev.typingUsers.includes(userName)
          ? prev
          : { typingUsers: [...prev.typingUsers, userName] }
      }
      return { typingUsers: prev.typingUsers.filter((n) => n !== userName) }
    }),
}))

/** Badge for the control bar: every thread's unread, summed. */
export function selectTotalUnread(s: ChatState): number {
  return Object.values(s.unreadPrivate).reduce((sum, n) => sum + n, s.unreadGroup)
}
